import { useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { EToastVariant, useToast } from '@/hooks/useToast';
import { clearJustLoggedIn, resetUser, selectAuth } from '@/redux/modules/auth';
import { triggerLoginModal } from '@/utility/triggerLoginModal';

interface EmailVerificationGuardProps {
  userFetchResult?: any;
}

/**
 * Component that checks if a user has an unverified email and logs them out
 * for security purposes, showing an appropriate error message.
 */
export const EmailVerificationGuard: React.FC<EmailVerificationGuardProps> = ({
  userFetchResult,
}) => {
  const intl = useIntl();
  const { showToast } = useToast();
  const dispatch = useDispatch();
  const auth = useSelector(selectAuth);

  // Clear justLoggedIn flag after a brief delay on page load to prevent race conditions
  useEffect(() => {
    if (auth.justLoggedIn) {
      const timer = setTimeout(() => {
        dispatch(clearJustLoggedIn());
      }, 5000); // Clear after 5 seconds
      return () => clearTimeout(timer);
    }
  }, [auth.justLoggedIn, dispatch]);

  useEffect(() => {
    // Don't log out if user just logged in (prevents race condition during verification completion)
    if (userFetchResult?.__unverified_email && !auth.justLoggedIn) {
      // Log out the user
      dispatch(resetUser());

      // Show error toast with clickable "log back in" link
      showToast(
        EToastVariant.Error,
        intl.formatMessage({
          id: 'PQp1q/',
          defaultMessage: 'Security Alert',
          description: 'Email Verification Guard > Security alert toast title',
        }),
        intl.formatMessage(
          {
            id: 'cx4pu7',
            defaultMessage:
              'For your security, you have been logged out. Please {loginLink} and follow the steps to verify your email.',
            description: 'Email Verification Guard > Unverified email logout message',
          },
          {
            loginLink: (
              <a
                href="#"
                onClick={e => {
                  e.preventDefault();
                  triggerLoginModal();
                }}
                style={{ textDecoration: 'underline', cursor: 'pointer' }}>
                <FormattedMessage
                  id="D6phz7"
                  defaultMessage="log back in"
                  description="Email Verification Guard > Log back in link text"
                />
              </a>
            ),
          },
        ),
      );
    }
  }, [userFetchResult, auth.justLoggedIn, dispatch, intl, showToast]);

  return null;
};
